import React, { useEffect } from 'react';

import { AlineTransforms, isParagraphElement, Queries } from '@aline/common';
import { Editor, Point, Range } from 'slate';
import { useEditorFindStateSelector } from 'src/editor/editor-find-state-registry';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useFocused } from 'src/layout-engine/hooks';
import { useEditingDisabled, useEditingMode } from 'src/store/document/selectors';

import { activeSearchHitVar, FindPopper, useActiveSearchHit } from './FindPopper';

export function EditorFindPopper(): JSX.Element | null {
  const editor = useEditor();
  const isEditorFocused = useFocused();
  const activeSearchHit = useActiveSearchHit();
  const currentUserId = useCurrentUserId();
  const editingDisabled = useEditingDisabled();
  const { editingMode } = useEditingMode();
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  const matchAccents = useEditorFindStateSelector(state => state.matchAccents);
  const matchCase = useEditorFindStateSelector(state => state.matchCase);
  const replace = useEditorFindStateSelector(state => state.replace);
  const search = useEditorFindStateSelector(state => state.search);
  const rWHeightPx = useEditorTabStateSelector(state => state.reactWindow.windowHeight);
  const rWOffsetPx = useEditorTabStateSelector(state => state.reactWindow.scrollOffset);

  const canReplace = currentUserId !== null && editingMode !== null && !editingDisabled;

  useEffect(() => {
    if (isEditorFocused) activeSearchHitVar(null);
  }, [isEditorFocused]);

  function getSearchHits(): Range[] {
    if (typeof search !== 'string') return [];

    const activeRootEntries = Queries.getActiveRootEntries(editor);
    const paragraphEntries = [...activeRootEntries].flatMap(([, path]) => [
      ...Editor.nodes(editor, { at: path, match: isParagraphElement }),
    ]);

    return paragraphEntries.flatMap(paragraphEntry =>
      Queries.getCMSearchHits({
        cMParagraphEntry: paragraphEntry,
        matchAccents,
        matchCase,
        search,
      })
    );
  }

  function onClose() {
    ClientTransforms.focus(editor);
  }

  function navigateToSearchHit(searchHit: Range): void {
    activeSearchHitVar(searchHit);
    ClientTransforms.scrollToLocation(editor, {
      cMLocation: searchHit,
      rWHeightPx,
      rWOffsetPx,
      select: false,
    });
  }

  function navigateToNextResult(): void {
    const searchHits = getSearchHits();
    if (!searchHits.length || !editor.selection) return;

    const [firstSearchHit] = searchHits;
    const [, end] = activeSearchHit ? Range.edges(activeSearchHit) : Range.edges(editor.selection);
    const nextSearchHit = searchHits.find(searchHit => {
      const [startHit] = Range.edges(searchHit);
      return !Point.isBefore(startHit, end);
    });
    if (!nextSearchHit) return navigateToSearchHit(firstSearchHit);

    return navigateToSearchHit(nextSearchHit);
  }

  function navigateToPreviousResult(): void {
    const reversedSearchHits = getSearchHits().reverse();
    if (!reversedSearchHits.length || !editor.selection) return;

    const [lastSearchHit] = reversedSearchHits;
    const [start] = activeSearchHit ? Range.edges(activeSearchHit) : Range.edges(editor.selection);
    const prevSearchHit = reversedSearchHits.find(searchHit => {
      const [, endHit] = Range.edges(searchHit);
      return !Point.isAfter(endHit, start);
    });
    if (!prevSearchHit) return navigateToSearchHit(lastSearchHit);

    return navigateToSearchHit(prevSearchHit);
  }

  function replaceCurrent() {
    if (!canReplace) return;

    const searchHitsBefore = getSearchHits();
    if (!searchHitsBefore.length) return;

    const hitToReplace = activeSearchHit ?? searchHitsBefore[0];
    const indexOfReplaced = searchHitsBefore.findIndex(h => Range.equals(h, hitToReplace));
    // Remember the hit before the replaced hit so we can reliably find the next hit after the replacement
    const hitBeforeReplaced = indexOfReplaced > 0 ? searchHitsBefore[indexOfReplaced - 1] : null;

    AlineTransforms.select(editor, hitToReplace);
    AuthorizedTransforms.insertTextAtSelection(editor, editingMode, currentUserId, replace);

    const searchHitsAfter = getSearchHits();
    if (!searchHitsAfter.length) {
      activeSearchHitVar(null);
      return;
    }

    if (hitBeforeReplaced) {
      const indexOfHitBeforeReplaced = searchHitsAfter.findIndex(h =>
        Range.equals(h, hitBeforeReplaced)
      );
      const nextHit =
        indexOfHitBeforeReplaced >= 0 ? searchHitsAfter[indexOfHitBeforeReplaced + 1] : null;
      if (nextHit) {
        navigateToSearchHit(nextHit);
        return;
      }
    }

    navigateToSearchHit(searchHitsAfter[0]);
  }

  function replaceAll() {
    if (!canReplace) return;

    const reversedSearchHits = getSearchHits().reverse();
    if (!reversedSearchHits.length) return;

    AlineTransforms.withoutNormalizing(editor, () => {
      // Replace hits from the end to the start to avoid the search hits changing position as we replace them
      reversedSearchHits.forEach(hit => {
        AlineTransforms.select(editor, hit);
        AuthorizedTransforms.insertTextAtSelection(editor, editingMode, currentUserId, replace);
      });
    });
    activeSearchHitVar(null);
  }

  if (typeof search !== 'string') return null;

  return (
    <FindPopper
      canMatchAccents
      canReplace={canReplace}
      navigateToNextResult={navigateToNextResult}
      navigateToPreviousResult={navigateToPreviousResult}
      onClose={onClose}
      onReplaceAll={replaceAll}
      onReplaceCurrent={replaceCurrent}
    />
  );
}
